import { supabase } from "./supabase";

/**
 * Sets up the initial admin user in the system
 * This should be called once during initial setup
 */
export const setupInitialAdmin = async (adminEmail: string = "joeri@met.care") => {
  try {
    console.log("Setting up initial admin user...");

    // First, check if the user exists in auth.users
    const { data: authUsers, error: authError } = await supabase.auth.admin.listUsers();

    if (authError) {
      console.error("Error fetching auth users:", authError);
      return { success: false, error: "Could not access user list" };
    }

    const authUser = authUsers.users.find(u => u.email === adminEmail);

    if (!authUser) {
      console.error(`Auth user with email ${adminEmail} not found`);
      return { success: false, error: `User ${adminEmail} not found in authentication` };
    }

    // Check if user profile exists
    const { data: existingUser, error: fetchError } = await supabase
      .from("users")
      .select("*")
      .eq("email", adminEmail)
      .single();

    if (fetchError && fetchError.code !== "PGRST116") {
      console.error("Error checking existing user:", fetchError);
      return { success: false, error: "Error checking existing user" };
    }

    if (existingUser) {
      // User exists, update role to admin
      const { error: updateError } = await supabase
        .from("users")
        .update({ role: "admin" })
        .eq("id", existingUser.id);

      if (updateError) {
        console.error("Error updating user role:", updateError);
        return { success: false, error: "Could not update user role" };
      }

      console.log(`Successfully updated ${adminEmail} to admin role`);
      return { success: true, message: `${adminEmail} is now an admin` };
    } else {
      // User doesn't exist in profiles, create them
      const { error: insertError } = await supabase
        .from("users")
        .insert({
          id: authUser.id,
          email: adminEmail,
          full_name: authUser.user_metadata?.full_name || "Admin User",
          role: "admin"
        });

      if (insertError) {
        console.error("Error creating user profile:", insertError);
        return { success: false, error: "Could not create user profile" };
      }

      console.log(`Successfully created admin profile for ${adminEmail}`);
      return { success: true, message: `Admin profile created for ${adminEmail}` };
    }
  } catch (error) {
    console.error("Error in setupInitialAdmin:", error);
    return { success: false, error: "Unexpected error during admin setup" };
  }
};

/**
 * Alternative setup that doesn't require admin API access
 * Uses RPC function or direct database access
 */
export const setupAdminUserProfile = async (adminEmail: string = "joeri@met.care") => {
  try {
    console.log("Setting up admin user profile...");

    // Get current user (should be the admin logging in)
    const { data: { user: currentUser } } = await supabase.auth.getUser();

    if (!currentUser || currentUser.email !== adminEmail) {
      return {
        success: false,
        error: `Please log in with the admin email (${adminEmail}) first`
      };
    }

    // Update or create the user profile
    const { data, error } = await supabase
      .from("users")
      .upsert({
        id: currentUser.id,
        email: adminEmail,
        full_name: currentUser.user_metadata?.full_name || "Admin User",
        role: "admin"
      }, {
        onConflict: "id"
      })
      .select()
      .single();

    if (error) {
      console.error("Error setting up admin profile:", error);
      return { success: false, error: "Could not set up admin profile" };
    }

    console.log("Admin profile set up successfully:", data);
    return { success: true, message: "Admin profile set up successfully", user: data };
  } catch (error) {
    console.error("Error in setupAdminUserProfile:", error);
    return { success: false, error: "Unexpected error during setup" };
  }
};

/**
 * Check if current user is admin
 */
export const checkAdminStatus = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      return { isAdmin: false, error: "Not authenticated" };
    }

    const { data: userProfile, error } = await supabase
      .from("users")
      .select("role")
      .eq("id", user.id)
      .single();

    if (error) {
      console.error("Error checking admin status:", error);
      return { isAdmin: false, error: "Could not check user role" };
    }

    return { isAdmin: userProfile.role === "admin", user: userProfile };
  } catch (error) {
    console.error("Error in checkAdminStatus:", error);
    return { isAdmin: false, error: "Unexpected error" };
  }
};
